// BusinessUnitsPage - Admin CRUD for the 3-level Multi-BU hierarchy.
// Sidebar - Administration - Business Units. Admin-only route.
// Layout: Left tree of existing units; Right create/edit form with parent picker.
import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Building2, Plus, Save, Trash2, Loader2,
  Network, MapPin, Info, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import BUTreeNode from '@/components/BUTreeNode';

const EMPTY_FORM = {
  id: null, name: '', code: '', level: 'business_unit', parent_id: '',
  country: '', base_airport_icao: '', description: '',
};

export default function BusinessUnitsPage() {
  const { api } = useAuth();
  const [tree, setTree] = useState([]);
  const [flat, setFlat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, l] = await Promise.all([
        api.get('/admin/business-units/tree'),
        api.get('/admin/business-units?active_only=false'),
      ]);
      setTree(t.data.tree || []);
      setFlat(l.data.units || []);
    } catch (e) {
      toast.error('Failed to load business units');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const companies = useMemo(() => flat.filter((u) => u.level === 'company'), [flat]);
  const businessUnits = useMemo(() => flat.filter((u) => u.level === 'business_unit'), [flat]);

  const startNew = () => {
    setSelected(null);
    setForm(EMPTY_FORM);
  };

  const startEdit = (node) => {
    setSelected(node);
    setForm({
      id: node.id,
      name: node.name || '',
      code: node.code || '',
      level: node.level,
      parent_id: node.parent_id || '',
      country: node.country || '',
      base_airport_icao: node.base_airport_icao || '',
      description: node.description || '',
    });
  };

  const availableParents = useMemo(() => {
    if (form.level === 'company') return [];
    if (form.level === 'business_unit') return companies;
    if (form.level === 'department') return businessUnits;
    return [];
  }, [form.level, companies, businessUnits]);

  const canSubmit = form.name.trim().length > 0 &&
    (form.level === 'company' || form.parent_id);

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim() || null,
        level: form.level,
        country: form.country.trim() || null,
        base_airport_icao: form.base_airport_icao.trim().toUpperCase() || null,
        description: form.description.trim() || null,
      };
      if (form.level !== 'company') payload.parent_id = form.parent_id;

      if (form.id) {
        // PUT can't change parent_id per backend rules.
        const putBody = { ...payload };
        delete putBody.level;
        delete putBody.parent_id;
        await api.put(`/admin/business-units/${form.id}`, putBody);
        toast.success('Business unit updated');
      } else {
        await api.post('/admin/business-units', payload);
        toast.success('Business unit created');
        startNew();
      }
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!form.id) return;
    if (!window.confirm(`Delete "${form.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/business-units/${form.id}`);
      toast.success('Business unit deleted');
      startNew();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="business-units-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Network className="w-6 h-6 text-cyan-400" />
            Business Units
          </h1>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">
            Model your organisation as a 3-level tree: <b>Company</b> → <b>Business Unit</b> → <b>Department</b>.
            Every future safety report, risk, CAPA and audit can be scoped to a leaf node.
          </p>
        </div>
        <Button
          onClick={startNew}
          className="bg-cyan-600 hover:bg-cyan-700"
          data-testid="bu-new-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Unit
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left — Tree */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Building2 className="w-4 h-4 text-cyan-400" />
              Organisation Tree
              <Badge variant="outline" className="ml-auto text-xs">{flat.length} nodes</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading tree…
              </div>
            ) : tree.length === 0 ? (
              <div className="text-center py-10 text-slate-500 text-sm border border-dashed border-slate-800 rounded">
                <Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No business units yet. Create the top-level <b>Company</b> node to get started.
              </div>
            ) : (
              <div className="space-y-0.5">
                {tree.map((n) => (
                  <BUTreeNode
                    key={n.id}
                    node={n}
                    depth={0}
                    onSelect={startEdit}
                    selectedId={form.id}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right — Form */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              {form.id ? <><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Edit unit</>
                       : <><Plus className="w-4 h-4 text-cyan-400" /> New unit</>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-300">Level *</Label>
              <Select
                value={form.level}
                onValueChange={(v) => setForm((f) => ({ ...f, level: v, parent_id: v === 'company' ? '' : f.parent_id }))}
                disabled={!!form.id}
              >
                <SelectTrigger className="bg-slate-950 border-slate-800 mt-1" data-testid="bu-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="company">Company (root)</SelectItem>
                  <SelectItem value="business_unit">Business Unit</SelectItem>
                  <SelectItem value="department">Department</SelectItem>
                </SelectContent>
              </Select>
              {form.id && (
                <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                  <Info className="w-3 h-3" /> Level cannot be changed after creation.
                </p>
              )}
            </div>

            {form.level !== 'company' && (
              <div>
                <Label className="text-slate-300">Parent *</Label>
                <Select
                  value={form.parent_id}
                  onValueChange={(v) => setForm((f) => ({ ...f, parent_id: v }))}
                  disabled={!!form.id}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-800 mt-1" data-testid="bu-parent-select">
                    <SelectValue placeholder={availableParents.length > 0 ? 'Select a parent' : 'Create a parent first'} />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {availableParents.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-slate-300">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Passenger Airline"
                className="bg-slate-950 border-slate-800 mt-1"
                data-testid="bu-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Code</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  placeholder="e.g. PAX"
                  className="bg-slate-950 border-slate-800 mt-1 font-mono"
                  data-testid="bu-code-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">ICAO</Label>
                <Input
                  value={form.base_airport_icao}
                  onChange={(e) => setForm((f) => ({ ...f, base_airport_icao: e.target.value.toUpperCase().slice(0,4) }))}
                  placeholder="OERK"
                  maxLength={4}
                  className="bg-slate-950 border-slate-800 mt-1 font-mono"
                  data-testid="bu-icao-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Country (ISO-2)</Label>
              <Input
                value={form.country}
                onChange={(e) => setForm((f) => ({ ...f, country: e.target.value.toUpperCase().slice(0,2) }))}
                placeholder="SA"
                maxLength={2}
                className="bg-slate-950 border-slate-800 mt-1 font-mono w-24"
                data-testid="bu-country-input"
              />
            </div>

            <div>
              <Label className="text-slate-300">Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional notes"
                className="bg-slate-950 border-slate-800 mt-1"
                data-testid="bu-description-input"
              />
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-slate-800">
              <Button
                onClick={submit}
                disabled={!canSubmit || saving}
                className="bg-cyan-600 hover:bg-cyan-700 flex-1"
                data-testid="bu-save-btn"
              >
                {saving
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  : <><Save className="w-4 h-4 mr-2" /> {form.id ? 'Update' : 'Create'}</>}
              </Button>
              {form.id && (
                <Button
                  variant="outline"
                  onClick={remove}
                  disabled={deleting}
                  className="border-red-800/50 text-red-300 hover:bg-red-500/10"
                  data-testid="bu-delete-btn"
                >
                  {deleting
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Trash2 className="w-4 h-4" />}
                </Button>
              )}
              {form.id && (
                <Button
                  variant="outline"
                  onClick={startNew}
                  className="border-slate-700 text-slate-200"
                  data-testid="bu-cancel-btn"
                >
                  Cancel
                </Button>
              )}
            </div>

            {selected && selected.path && selected.path.length > 0 && (
              <div className="text-[11px] text-slate-500 flex items-center gap-1 pt-2">
                <MapPin className="w-3 h-3" /> Path: {selected.path.map((id) => flat.find((u) => u.id === id)?.name || id).join(' → ')} → {selected.name}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
